import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as routes from "../../../routes";
import {
  GenericTable,
  Spinner,
  Pagination,
  SearchableDropdown,
  Tooltip,
} from "../../../Components/Common";
import {
  LEDGER_COLUMNS,
  LEDGER_LABELS,
  MISC,
  TRANSACTION_TYPE_LEDGER,
  TRANSACTION_TYPE_LEDGER_LABEL,
} from "./constants";
import { useLedger } from "../Hooks/useLedger";
import { formatDate } from "../../../Utils/timeUtils";
import { PROJECT_TYPES, CATEGORY_TYPES } from "../../Project/constant";
import { FaLongArrowAltUp, FaLongArrowAltDown } from "react-icons/fa";
import { SquareArrowOutUpRight } from "lucide-react";
import { formatDateDDMMYYYY, toProperCase } from "../../../Utils";
import { FiInfo } from "react-icons/fi";

const ProjectLedger = () => {
  const {
    loading,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchProjectLedger,
    fetchAllProjects,
  } = useLedger();

  const [projects, setProjects] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [dateRange, setDateRange] = useState({
      from: null,
      to: null,
    });

  const navigate = useNavigate();

  const filterStaticOptions =
    (options) =>
    async (params = {}) => {
      const search = params?.search?.toLowerCase?.() || "";
      return options.filter((opt) => opt.label.toLowerCase().includes(search));
    };

  const getTypeOptions = filterStaticOptions(PROJECT_TYPES);
  const getCategoryOptions = filterStaticOptions(CATEGORY_TYPES);

  const fetchFilteredProjects = async () => {
    const projectList = await fetchAllProjects({
      category: selectedCategory,
      type: selectedType,
    });

    setProjects(projectList || []);
    setSelectedProject(null);
    setLedgerData([]);
  };

  const loadProjects = async () => {
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (selectedType) params.type = selectedType;

    const projectList = await fetchAllProjects(params);
    setProjects(projectList || []);
  };

const loadLedger = async (projectId) => {
  if (!projectId || !selectedCategory || !selectedType) return;

  const params = {
    project_id: projectId,
    category: selectedCategory,
    type: selectedType,
  };

  if (dateRange.from && dateRange.to) {
    params.transaction_date_after = dateRange.from;
    params.transaction_date_before = dateRange.to;
  }

  const data = await fetchProjectLedger(params);
  setLedgerData(data || []);
};


  useEffect(() => {
    loadProjects();
  }, []);

  // When category or type changes then clear old project, fetch new filtered projects
  useEffect(() => {
    if (!selectedCategory || !selectedType) {
      setProjects([]);
      setSelectedProject(null);
      setLedgerData([]);
      return;
    }
    fetchFilteredProjects();
  }, [selectedCategory, selectedType]);

  // When project changes then load its ledger
  useEffect(() => {
    if (selectedProject?.id) {
      loadLedger(selectedProject.id);
    }
  }, [selectedProject, selectedCategory, selectedType, page, rowsPerPage, dateRange.from, dateRange.to]);

  const renderField = (key, row) => {
    switch (key) {
      case "approved_on":
        const date = row.approved_on.split("T")[0];
        return formatDateDDMMYYYY(date);

      case "transaction_amount":
        return (
          <span className="font-medium text-gray-800">
            ₹{row.transaction_amount?.toLocaleString("en-IN")}
          </span>
        );
      case "before_amount":
        return (
          <span className="font-medium text-gray-800">
            ₹{row.before_amount?.toLocaleString("en-IN")}
          </span>
        );

      case "balance":
        return (
          <span
            className={`font-semibold ${
              row.balance >= 0 ? "text-green-500" : "text-red-500"
            }`}
          >
            ₹{row.balance?.toLocaleString("en-IN")}
          </span>
        );

      case "note_type":
        return (
          <span
            className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${
              row.note_type === TRANSACTION_TYPE_LEDGER.BILL
                ? "bg-orange-100 text-orange-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {toProperCase(row.note_type)}
          </span>
        );

      case "actions":
        return (
          <button
            className="text-blue-600 cursor-pointer hover:text-blue-800 transition-colors duration-200"
            onClick={() =>
              navigate(
                row.note_type === TRANSACTION_TYPE_LEDGER.BILL
                  ? routes.VIEW_BILL.replace(":billId", row.entity_id)
                  : routes.VIEW_ADVANCE_NOTE.replace(
                      ":instanceId",
                      row.entity_id
                    )
              )
            }
            title="View Details"
          >
            <SquareArrowOutUpRight size={18} />
          </button>
        );

      default:
        return <span>{row[key] || "-"}</span>;
    }
  };

  const renderMobileField = (row) => (
    <div
      key={row.linked_to}
      className="rounded-xl p-3 shadow-sm border border-gray-100 bg-white hover:shadow-md transition-all cursor-pointer mb-2"
      onClick={() =>
        navigate(
          row.note_type === TRANSACTION_TYPE_LEDGER.BILL
            ? routes.VIEW_BILL.replace(":billId", row.entity_id)
            : routes.VIEW_ADVANCE_NOTE.replace(":instanceId", row.entity_id)
        )
      }
    >
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {row.site_name || MISC.NOT_AVAILABLE}
          </span>
          <span
            className={`text-[10px] px-2 py-[2px] rounded-md font-medium ${
              row.note_type === TRANSACTION_TYPE_LEDGER.BILL
                ? "bg-orange-100 text-orange-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {toProperCase(row.note_type)}
          </span>
        </div>
        <span className="text-[11px] text-gray-500">
          {formatDateDDMMYYYY(row.approved_on.split("T")[0])}
        </span>
      </div>

      <div className="flex justify-between items-center mt-1">
        <span className="text-base font-semibold text-gray-900">
          ₹{row.transaction_amount?.toLocaleString("en-IN")}
        </span>

        <div className="flex items-center gap-1">
          {row.balance < 0 ? (
            <FaLongArrowAltDown size={10} className="text-red-700 text-sm" />
          ) : (
            <FaLongArrowAltUp size={10} className="text-green-700 text-sm" />
          )}
          <span
            className={`text-[12px] font-semibold  ${
              row.balance < 0 ? "text-red-700 " : "text-green-700 "
            }`}
          >
            ₹{Math.abs(row.balance)?.toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-semibold">Project Ledger</h2>
      </div>

      <div className="flex flex-wrap bg-white w-full rounded-2xl shadow-md border px-4 py-3 mb-4 gap-1">
        <div className="flex flex-wrap gap-4 mt-6 mr-4">
          <SearchableDropdown
            fetchFunction={getTypeOptions}
            valueKey="value"
            labelKey="label"
            onSelect={(val) => setSelectedType(val)}
            placeholder="Select Project Type"
            width="64"
          />

          <SearchableDropdown
            fetchFunction={getCategoryOptions}
            valueKey="value"
            labelKey="label"
            onSelect={(val) => setSelectedCategory(val)}
            placeholder="Select Project Category"
            width="64"
          />

          <SearchableDropdown
            fetchFunction={
              selectedCategory && selectedType
                ? async (params) =>
                    await fetchAllProjects({
                      ...params,
                      category: selectedCategory,
                      type: selectedType,
                    })
                : async () => []
            }
            onSelect={(val) => {
              const project = projects.find((p) => p.id === val);
              setSelectedProject(project || null);
            }}
            valueKey="id"
            labelKey="name"
            includeAllOption={false}
            placeholder="Select Project"
            width="64"
            disabled={!selectedCategory || !selectedType}
          />
        </div>

        {/* Start Date */}
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={dateRange.from || ""}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, from: e.target.value }))
            }
            className="w-36 md:w-40 border-gray-300 rounded-lg px-3 py-2 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400"
            disabled={!selectedProject}
          />
        </div>

        <div className="flex flex-col justify-center items-center relative m-1">
          <span className="text-gray-500 font-semibold text-lg mt-4">-</span>
        </div>

        {/* End Date */}
        <div className="flex flex-col">
          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
            End Date{" "}
            <Tooltip text={"Select both dates for custom transaction"}>
              <FiInfo
                size={10}
                className="cursor-pointer text-primary hover:text-secondary text-lg"
              />
            </Tooltip>
          </label>
          <input
            type="date"
            value={dateRange.to || ""}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, to: e.target.value }))
            }
            className="w-36 md:w-40 border-gray-300 rounded-lg px-3 py-2 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400"
            disabled={!selectedProject || !dateRange.from}
          />
        </div>
      </div>

      {!selectedCategory || !selectedType ? (
        <div className="text-center text-base text-gray-500 mt-10">
          ⚠️ Select <span className="font-semibold">Category</span> and{" "}
          <span className="font-semibold">Type</span> first to enable the
          Project filter.
        </div>
      ) : !selectedProject ? (
        <div className="text-center text-base text-gray-500 mt-10">
          📁 Select a <span className="font-semibold">Project</span> to view its
          ledger.
        </div>
      ) : loading && ledgerData.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Spinner size={4} />
        </div>
      ) : ledgerData.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {MISC.NO_PROJECT_LEDGER_FOUND}
        </div>
      ) : (
        <>
          <div className="overflow-y-auto max-h-[50vh] pb-4 rounded-md relative">
            <GenericTable
              tableColumns={LEDGER_COLUMNS}
              tableRows={ledgerData}
              columnLabels={LEDGER_LABELS}
              renderField={renderField}
              renderMobileCard={renderMobileField}
              mobileGridColumns={2}
            />
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default ProjectLedger;
